import { PATHS } from '@ctcells/core'
import { t } from '@ctcells/i18n'
import { lazy, Suspense } from 'react'
import { useNavigate, useParams } from 'react-router'

import { cx } from '../atoms/cx.ts'
import { AdminContainer, UserDetailContainer } from '../screens/admin/AdminContainer.tsx'
import { ScreenLoading } from '../screens/ScreenState.tsx'

/** 웹훅 상세는 에러 코드 사전(약 260KB)을 쓰는 알람별 전달 표를 품어 판을 열 때만 받는다(`AlarmSettingsContainer` 와 같은 까닭) */
const WebhookDetailContainer = lazy(() =>
  import('../screens/admin/WebhookDetailContainer.tsx').then((m) => ({
    default: m.WebhookDetailContainer,
  })),
)

/**
 * 관리 › 사용자의 **목록 + 오른쪽 상세 판**(eric 2026-09-22 「관리에서 아까와 마찬가지로 모듈과
 * 사용자 부분에 해당 row 를 클릭하면 상세 페이지 내용이 보이게 해줘」).
 * 준비 3종의 `SamplesSplitRoute` 와 같은 뼈대다.
 *
 * - 주소는 종전 그대로(`/admin` · `/admin/members/:userId`)다. 새로고침·뒤로가기·북마크가 다 산다
 * - 목록은 늘 서고, `:userId` 가 있으면 오른쪽 판이 열린다. 다른 행을 누르면 판의 내용만 바뀐다
 *   (`key` 로 판을 새로 세워 열림 움직임이 다시 난다)
 * - 관리 탭 줄(사용자·설정·모듈·로그)은 목록 쪽에 있어 판이 열려도 그대로 선다
 * - 🔴 상세는 **같은 컨테이너**다. 판에 맞춰 두 벌을 만들지 않는다 — 장비 관점 콘솔은 그 컨테이너를
 *   prop 없이 전체 화면으로 쓴다(`apps/console-equipment/src/routes.tsx`)
 *
 * ⚠ `:userId` 의 실제 값은 **`username`** 이다(표 링크 · `UserDetailScreen` 둘 다 그 축으로 찾는다).
 *   `user_id` 는 UUID 와 문자열(`"dev-admin"`)이 섞여 있어 주소에 쓰지 않는다.
 *
 * 라우트 표(`consoleResourceRoutes.tsx`)와 파일을 나눈 것은 한 파일이 컴포넌트만 내보내야
 * 핫리로드가 성립해서다.
 */
export function MembersSplitRoute() {
  const navigate = useNavigate()
  const { userId } = useParams()
  return (
    /*
     * 🔴 **사용자 상세만 종전 폭(약 1/2)이다**(`splitpage--half`). 서랍이 1/3 로 좁아질 때(eric 2026-09-30) 역할 고르기
     *    (viewer · operator · admin)와 활성 세션 표가 서랍 밖으로 넘쳤다(1280 실측 469/343px).
     */
    <div className={cx('splitpage', 'splitpage--half', userId !== undefined && 'splitpage--open')}>
      <AdminContainer selectedUserId={userId} tab="members" />
      {userId === undefined ? null : (
        <aside aria-label={t('screen.userDetail.title')} className="splitpage__pane" key={userId}>
          <UserDetailContainer onClose={() => void navigate(PATHS.admin)} />
        </aside>
      )}
    </div>
  )
}

/**
 * 관리 › 웹훅의 **목록 + 오른쪽 상세 판**(eric 2026-10-29 「이거 웹훅도 마찬가지로.. 웹훅 리스트를 보여주고.. 게시판처럼 + 웹훅 추가가
 * 오른쪽에 있게 해줘.. 그리고 해당 웹훅을 클릭하면 상세페이지에 상세 설정 내용이 보이게 해줘」). 사용자 탭(`MembersSplitRoute`)과 같은 뼈대다.
 *
 * - 주소 `/admin/webhooks` · `/admin/webhooks/:recipientId` — 새로고침 · 뒤로가기가 산다. 다른 행을 누르면 판 내용만 바뀐다(`key`)
 * - 판 폭은 사용자 상세와 같은 단(`splitpage--half`) — 판 안에 알람별 전달 표(묶음 · 스위치)가 서서 1/3 로는 좁다
 */
export function WebhooksSplitRoute() {
  const navigate = useNavigate()
  const { recipientId } = useParams()
  return (
    <div
      className={cx('splitpage', 'splitpage--half', recipientId !== undefined && 'splitpage--open')}
    >
      <AdminContainer tab="webhooks" />
      {recipientId === undefined ? null : (
        <aside
          aria-label={t('alarmSettings.hooks.detailTitle')}
          className="splitpage__pane"
          key={recipientId}
        >
          <Suspense fallback={<ScreenLoading />}>
            <WebhookDetailContainer
              onClose={() => void navigate(PATHS.adminWebhooks)}
              recipientId={recipientId}
            />
          </Suspense>
        </aside>
      )}
    </div>
  )
}
